import { toast } from "react-toastify";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputLabel,
    Paper,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    RadioGroup,
    FormControlLabel,
    Radio
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { useParams } from 'react-router-dom';
import { Link, useNavigate } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { isEmpty } from "../../../utils/Util";
import { StyleCreator } from "../../../helpers/stylecreator";
import { FormStyleCollection as formStyles } from "../../../styles/form";
import {
    ICreatePricingModelRequest,
    IMerchantAccount,
    IMerchantPaymentMethod,
    IPricingType,
    ISaveablePricingModel,
    IUpdatePricingModelRequest
} from "../../../types/types";


/**local*/
import {
    useLazyGetMerchantAccountsListQuery,
    useCreateSplitConfigurationProfileMutation,
    useLazyGetMerchantPaymentMethodListQuery,
    useGetPricingModelDetailsQuery,
    useUpdateSplitConfigurationProfileMutation
} from "../../../services/API/perseusPayAPISlice";

import ConfirmDialog from "../../../common/components/ConfirmDialog";



const defaultPricingModel: ISaveablePricingModel = {
    name: "",
    description: "",
    pricingType: "",
    merchantAccount: "",
    markupRate: null,
    markupFee: null,
    monthlyFee: 0.0,
    currency: "", // default; can later be set based on merchant account
    splitCofigurationId: "",
    pricingModelId: 0,
    achMarkupFee: null,
    achMarkupRate : null 
};

enum EditOptions {
    NONE = "0",
    ALL = "1",
    ACH_AND_NAME_ONLY = "2",
    NAME_ONLY = "3"
}

const SplitConfigurationMaster = () =>{
    const option_any: string = "ANY";
    const pricingmode_simple: string = "Simple";
    const pricingmode_enhanced: string = "Enhanced";
    const classes = StyleCreator.makeStyles(formStyles.all);
    const [pricingModel, setPricingModel] = useState<ISaveablePricingModel>(
        defaultPricingModel
    );
   
    const paymentMethod_ACH: string = "ach";
    const paymentMethod_Interac: string = "interac_card";
    const cardType_debit: string = "debit";
    const paymentMethod_ACH_DisplayName: string = "ACH Direct Debit";
    const [dialogProps, setDialogProps] = useState({
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
        confirmColor: "error" as
            | "inherit"
            | "primary"
            | "secondary"
            | "success"
            | "error"
            | "info"
            | "warning",
        showConfirm: true,
        isProcessing: false,
        processingContent: ""
    });

    const [onConfirm, setOnConfirm] = useState<() => void>(() => () => { });

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogContent, setDialogContent] = useState("");
    const [pricingMode, setPricingMode] = useState(pricingmode_simple);
    
    const [editOption, setEditOption] = useState<EditOptions>(EditOptions.ALL); // Ensure the state is initialized properly
    const [showPrcingTab, setShowPrcingTab] = useState<boolean>(false); // Ensure the state is initialized properly
    const [saveButtonTitle, setSaveButtonTitle] = useState("Save");
    const navigate = useNavigate();
    
    
    // Get the id from URL
    const { id, splitconfigid } = useParams<{ id: string; splitconfigid: string }>();
  
    // Conditionally fetch pricing model details if id is provided
    const {
        data,
        isLoading: isLoadingPricing,
        error: pricingError,
    } = useGetPricingModelDetailsQuery(
        { pricingModelId: id!, splitConfigurationId: splitconfigid! },
        { skip: !id || !splitconfigid }   // ✅ single boolean
    );

    ///

    // Merchant Accounts state
    // RTK Query lazy hook
    const [getMerchantAccounts] = useLazyGetMerchantAccountsListQuery();
    const [getMerchantPaymentMethods] = useLazyGetMerchantPaymentMethodListQuery();

    const [createSplitConfigurationProfile, { isLoading, isSuccess, isError, error }] = useCreateSplitConfigurationProfileMutation();
    const [updateSplitConfigurationProfile] = useUpdateSplitConfigurationProfileMutation();

    const [merchantAccountData, setMerchantAccountData] = useState<IMerchantAccount[]>([]);
    const [merchantPaymentMethods, setMerchantPaymentMethods] = useState<IMerchantPaymentMethod[]>([]);

    const [activeTab, setActiveTab] = useState(0);
    //const handleTabChange = (_: any, newValue: number) =>

    const handlePricingModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPricingMode(event.target.value);
        if (event.target.value === pricingmode_simple) {
            setShowPrcingTab(false);
            setSaveButtonTitle("Save");
        } else if (event.target.value === pricingmode_enhanced) {
            setShowPrcingTab(true);
            setSaveButtonTitle("Save & Next");
        }        
            
    };

    const handleTabChange = (_: any, newValue: number) => {
        if (newValue > 0 && !CheckData())
        {
            setActiveTab(0);
            return;
        }
        if (isEditMode && !pricingModelRequest_Update) // When in edit mode, user directly clicks the tab instead of save and next 
            SavePricingModelDefinition();
        setActiveTab(newValue);
    };

    

    const [merchantId, setMerchantId] = useState<IMerchantAccount>({
        merchantID: "",
        name: "",
        currency :""
    });

    const [pricingType, setPricingType] = useState<IPricingType>({
        description: "",
        name: ""
    });

   


    // Default header row (Any)
    const [rows, setRows] = useState([

        {
            cardType: "Any",
            brand: "Any",
            interaction: "Any",
            paymentMethod: "Any",
            markupRate: pricingModel?.markupRate?.toString(),
            markupFee: pricingModel?.markupFee?.toString(),
            fixed: true, // first row cannot be deleted
            ruleId: "",
            splitLogicId: "",
            hasError: false, // first row cannot be deleted
        },
    ]);
    
    // Dropdown options
    const [isFetchingData, setIsFetchingData] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [pricingTypeLabel, setPricingTypeLabel] = useState<any>("");
    const [pricingModelRequest, setPricingModelRequest] = useState<ICreatePricingModelRequest>(null);
    const [pricingModelRequest_Update, setPricingModelRequest_Update] = useState<IUpdatePricingModelRequest>(null);

    const [isEditMode, setEditMode] = useState<boolean>(false);


    const onNameChanged = (event: any) => {
        const { value } = event.target;

        setPricingModel((prev) => ({
            ...prev,          // keep other properties unchanged
            name: value,      // update only the 'name' property
        }));
    };


    const onDescriptionChanged = (event: any) => {
        const { value } = event.target;
        setPricingModel((prev) => ({
            ...prev,          // keep other properties unchanged
            description: value,      // update only the 'description' property
        }));
    };




    const onMarkupRateChanged = (event: any) => {
        const { value } = event.target;

        if (/^\d*\.?\d*$/.test(value)) {
            setPricingModel((prev) => ({
                ...prev,          // keep other properties unchanged
                markupRate: value,      // update only the 'markupRate' property
            }));

            // Update the 'fixed: true' row in rows
            setRows((prevRows) =>
                prevRows.map((row) =>
                    row.fixed ? { ...row, markupRate: value } : row
                )
            );
        }
    };

    const onMarkupFeeChanged = (event: any) => {
        const { value } = event.target;
        if (/^\d*\.?\d*$/.test(value)) {
            setPricingModel((prev) => ({
                ...prev,          // keep other properties unchanged
                markupFee: value,      // update only the 'markupFee' property
            }));

            // Update the 'fixed: true' row in rows
            setRows((prevRows) =>
                prevRows.map((row) =>
                    row.fixed ? { ...row, markupFee: value } : row
                )
            );
        }
    };

    const onMonthlyFeeChanged = (event: any) => {
        const { value } = event.target;
        if (/^\d*\.?\d*$/.test(value)) {
            setPricingModel((prev) => ({
                ...prev,          // keep other properties unchanged
                monthlyFee: value,      // update only the 'monthlyFee' property
            }));
        }
    };


    const setPricingTypeId = (value: any) => {
        setPricingType(value);
        setPricingModel((prev) => ({
            ...prev,          // keep other properties unchanged
            pricingType: value.name,      // update only the 'pricingType' property
        }));

        setPricingTypeLabel(value.name)
    };

    const setMerchantAccount = (value: IMerchantAccount) => {
        setMerchantId(value);
        setPricingModel((prev) => ({
            ...prev,          // keep other properties unchanged
            merchantAccount: value.merchantID,      // update only the 'merchantAccount' property
            currency: value.currency   // update only the 'currency ' property
        }));
    };

    // Check Save button Disabled or not
    const isSaveDisabled = useMemo(() => {
        if (isEmpty(pricingModel.name)) return true;
        if (isEmpty(pricingModel.description)) return true;
        if (isEmpty(pricingModel.merchantAccount)) return true;
        if (isEmpty(pricingModel.markupRate)) return true;
        if (isEmpty(pricingModel.markupFee)) return true;
        if (isEmpty(pricingModel.monthlyFee)) return true;
        return false;
    }, [pricingModel]);


    // Create Pricing Model Request


    function CreatePricingModel(pricingModel: any) {
        const payload: ICreatePricingModelRequest = {
            name: pricingModel.name,
            description: pricingModel.description,
            pricingType: pricingModel.pricingType,
            merchantId: pricingModel.merchantAccount,
            currency: pricingModel.currency,
            markupRate: pricingModel.markupRate,
            markupFee: pricingModel.markupFee,
            monthlyFee: pricingModel.monthlyFee,
            achMarkupFee: pricingModel.achMarkupFee,
            achMarkupRate: pricingModel.achMarkupRate,
            surchargeFee: "0",
            isSimple: (pricingMode === pricingmode_simple),
            pricings: [    // Default Pricing Rule
                {
                    cardBrand: "Any",
                    paymentMethod: "Any",
                    fundingSource: "Any",
                    shopperInteraction:"Any",
                    markupFee: pricingModel.markupFee,
                    markupRate: pricingModel.markupRate,
                    isDefault: true,
                    ruleId: "",
                    splitLogicId: ""
                }
            ]
        };
        //Set row in grid for next step
        setACHRow(pricingModel);
        setPricingModelRequest(payload)
        return true;
    }

    function UpdatePricingModel(pricingModel: any) {
        const payload: IUpdatePricingModelRequest = {
            name: pricingModel.name,
            description: pricingModel.description,
            pricingType: pricingModel.pricingType,
            merchantId: pricingModel.merchantAccount,
            currency: pricingModel.currency,
            markupRate: pricingModel.markupRate,
            markupFee: pricingModel.markupFee,
            monthlyFee: pricingModel.monthlyFee,
            achMarkupFee: pricingModel.achMarkupFee,
            achMarkupRate: pricingModel.achMarkupRate,
            surchargeFee: "0",
            pricingModelId: pricingModel.pricingModelId,
            splitCofigurationId: pricingModel.splitCofigurationId,
            isSimple: (pricingMode === pricingmode_simple),
            pricings: []
        };
        //Set row in grid for next step
        setACHRow(pricingModel);
        setPricingModelRequest_Update(payload)
        return true;
    }

    function CreatePricingModelRules() {       
        let pricings = null;
        if (isEditMode && pricingModelRequest_Update) {
            pricings = pricingModelRequest_Update.pricings;
        }
        else if (!isEditMode && pricingModelRequest) {
            pricings = pricingModelRequest.pricings;
        }
        //This will add the rule ach pricing 
        setACHRowToPricing(pricings);
        rows.filter((row) => !row.fixed) // exclude fixed rows
            .map((row) => (
                pricings.push({
                    cardBrand: row.brand,
                    paymentMethod: row.brand,
                    fundingSource: row.cardType,
                    shopperInteraction: row.interaction,
                    markupFee: row.markupFee,
                    markupRate: row.markupRate,
                    isDefault: false,
                    ruleId: row.ruleId,
                    splitLogicId: row.splitLogicId
                })
            )
            )
    }
    function CheckData(): boolean {

        if (isEmpty(pricingModel.name)) {
            toast.error("Name required");
            return false;
        }

        if (isEmpty(pricingModel.description)) {
            toast.error("Description required");
            return false;
        }

        if (isEmpty(pricingModel.merchantAccount)){
            toast.error("Merchant Account required");
            return false;
        }

        if (isEmpty(pricingModel.pricingType)) {
            toast.error("Pricing Type required");
            return false;
        }

        if (isEmpty(pricingModel.markupRate)) {
            toast.error("Markup Rate required");
            return false;
        }

        if (pricingModel.markupRate >= 10) {
            toast.error("Invalid Markup rate. Max 9.9");
            return false;
        }

        if (isEmpty(pricingModel.markupFee) ) {
            toast.error("Markup Fee required");
            return false;
        }

        if (pricingModel.markupFee >= 10) {
            toast.error("Invalid Markup fee. Max 9.9");
            return false;
        }

        if (pricingModel.monthlyFee >= 1000) {
            toast.error("Invalid Monthly fee. Max 999.99");
            return false;
        }

        if (!isEmpty(pricingModel.achMarkupFee) && pricingModel.achMarkupFee >= 10) {
            toast.error("Invalid ACH Markup fee. Max 9.9");
            return false;
        }

        if (!isEmpty(pricingModel.achMarkupRate) && pricingModel.achMarkupRate >= 10) {
            toast.error("Invalid ACH Markup rate. Max 9.9");
            return false;
        }
        return true;
    }
    function CheckPricingData(): boolean {
        ///
        const updatedRows = [...rows];  // PRESERVE EXISTING ROWS

        const invalidRows = [];         // INVALID ROWS COLLECTION
        const duplicates: number[] = []; // store indices of duplicate rows
        const seen = new Map<string, number>();

        let fixedDefaultRow: any = null;
        rows.forEach((row, index) => {


            //Set interaction to default ANY for INTERCHANGE PLUS
            if (
                pricingModel?.pricingType &&              // ensures pricingType exists
                pricingModel?.pricingType.toUpperCase() === "INTERCHANGE PLUS"
            ) {
                if (!row.fixed) {
                    row.interaction = option_any;
                }
            }

            const key = `${row.cardType.toUpperCase()}-${row.brand.toUpperCase()}-${row.interaction.toUpperCase()}`; //KEY FOR DEUPLICATE VALIDATION

            if (row.fixed) {
                fixedDefaultRow = row;
                seen.set(key, index); // add fixed row to seen collection
                return;
            }


            let invalid =
                (isEmpty(row.markupFee) || row.markupFee === null || Number(row.markupFee) >= 10) ||
                (isEmpty(row.markupRate) || row.markupRate === null || Number(row.markupRate) >= 10) ||
                (isEmpty(row.cardType) || row.cardType === "none" ) ||
                (isEmpty(row.interaction) || row.interaction === "none") ||
                (isEmpty(row.brand) || row.brand === "none");

            if (row.cardType.toUpperCase() === option_any || row.interaction.toUpperCase() === option_any || row.brand.toUpperCase() === option_any) {
                if (
                    Number(row.markupFee) === Number(fixedDefaultRow.markupFee) &&
                    Number(row.markupRate) === Number(fixedDefaultRow.markupRate)
                ) {
                    invalid = true;
                }
            }

            if (invalid) {
                invalidRows.push(row);
            }
            if (seen.has(key)) {
                // Duplicate found
                invalid = true;
                duplicates.push(index);
            } else {
                // First occurrence
                seen.set(key, index);
            }

            updatedRows[index] = {
                ...row,
                hasError: invalid,
            };
        });

        if (invalidRows.length > 0 || duplicates.length > 0) {
            setRows(updatedRows);
            toast.error("Some rows have invalid or duplicated values");
            return false;
        }
        return true;
    }

    //Step 1 : Details.
    const [isMerchantPaymentMethodsLoading, setIsMerchantPaymentMethodsLoading] = useState(false);

    const fetchMerchantPaymentMetodData = async () => {
        try {
            setIsMerchantPaymentMethodsLoading(true); // start loading
            const result = await getMerchantPaymentMethods({ merchantId: pricingModel.merchantAccount }).unwrap();
            setMerchantPaymentMethods(result || []);
        } catch (err) {
            toast.error("Failed to load merchant accounts");
        } finally {
            setIsFetchingData(false);
             }
    }

    async function SavePricingModelDefinition() {
        toast.dismiss();
        setIsProcessing(true);

        if (!CheckData()) {
            setIsProcessing(false);
            return;
        }

        setIsProcessing(true);
        if (isEditMode && !isEmpty(splitconfigid)) {
            UpdatePricingModel(pricingModel)
        }
        else {
            CreatePricingModel(pricingModel);
        }

        //if pricing mode is simple, do not go to next tab and save the data directly 
        // the data will be saved using the  hook at line #679
        if (pricingMode === pricingmode_simple || (pricingMode !== pricingmode_simple && editOption !== EditOptions.ALL)) {
            return;
        }
        setIsProcessing(false);
        fetchMerchantPaymentMetodData();
        setActiveTab(1);

    }

    //Save to DB - Sent API Call 
    async function SavePricingModelRulesDefinition() {
        toast.dismiss();
        setIsProcessing(true);
      
        if (!CheckData()) {
            setIsProcessing(false);
            return;
        }

        if (!CheckPricingData()) {
            setIsProcessing(false);
            return;
        }
        setIsProcessing(true);
        CreatePricingModelRules();

        setDialogOpen(true);
        setDialogTitle("Saving Pricing Model");
        //setDialogContent("Creating new pricing model");
        setDialogProps({
            confirmLabel: "OK",
            cancelLabel: "Cancel",
            confirmColor: "error",
            showConfirm: false,
            isProcessing: true,
            processingContent: "Please wait ......"
        });
        if (isEditMode && !isEmpty(splitconfigid)) {
            try {
                
                let res = await updateSplitConfigurationProfile(pricingModelRequest_Update).unwrap();
                if (res) {
                    if (res.error) {
                        toast.error(res.error?.errorMessage);
                    }
                    else {                        
                        toast.success("Pricing model updated succesfully");
                        navigate(`../pricingmodel`);  // ✅ navigate 
                    }
                }
            } catch (err) {
                if (err) {
                    if (err.data.response) {
                        toast.error(err.data.response.error.errorMessage);
                    }
                    else {
                        toast.error("failed to edit pricing model");
                    }
                }
            }
        } else {
            let errorMessage = "Failed to save pricing model";
            try {               
                let res = await createSplitConfigurationProfile(pricingModelRequest).unwrap();
                if (res) {
                    if (res.error) {
                        errorMessage = res.error?.errorMessage;
                        toast.error(errorMessage);
                    }
                    else {
                        toast.success("Pricing model created succesfully");
                        navigate(`../pricingmodel`);  // ✅ navigate 
                    }                    
                }
            } catch (err) {
                if (err) {
                    if (err.data) {
                        errorMessage = err?.data?.error?.errorMessage;
                    }
                    if (isEmpty(errorMessage)) {
                        errorMessage = err?.data?.title;
                    }
                }
                toast.error(errorMessage);
            }
        }
        setDialogOpen(false);
        setIsProcessing(false);
    }
 
    // Fetch Merchant Accounts on mount only if id is not provided / create new mode 
    useEffect(() => {        
        if (!id) {
            document.title = "Create Pricing Model";
            const fetchData = async () => {
                setIsFetchingData(true);
                try {
                    const result = await getMerchantAccounts(null).unwrap();
                    setMerchantAccountData(result || []);
                } catch (err) {
                    toast.error("Failed to load merchant accounts");
                } finally {
                    if (!id)
                        setIsFetchingData(false);
                }
            };
            fetchData();
        }
    }, [id,getMerchantAccounts]);

   

    // Set page title based on mode
    useEffect(() => {
        document.title = id ? "Edit Pricing Model" : "Create Pricing Model";
        if (id)
            setIsFetchingData(true);
       
    }, [id]);

    // Populate state in edit mode
    useEffect(() => {
        if (data) {
            
            let details = data.details;
            setEditMode(true);
            let achRuleFound: boolean = false;
            let rules = data.rules;
            let achRule = rules.filter(rule => rule.paymentMethod === paymentMethod_ACH);
            var achMarkupRate = null; 
            var achmarkupFee = null;
            if (achRule && achRule.length > 0) {
              achmarkupFee = achRule[0].markupFee;
              achMarkupRate = achRule[0].markupRate;
              achRuleFound = true;

              // Move the first ACH rule to the top of the rules array
              rules = moveItemToTopByValue(rules, achRule[0]);
            }
            
            setPricingModel({
                name: details.name ?? "",
                description: details.description ?? "",
                pricingType: details.pricingType ?? "",
                merchantAccount: details.merchantName ?? "",
                markupRate: details.markupRate ?? 0.0,
                markupFee: details.markupFee ?? 0.0,
                monthlyFee: details.monthlyFee ?? 0.0,
                currency: details.currency ?? "",
                splitCofigurationId: details.splitConfigurationId ?? "",
                pricingModelId: details.id,
                achMarkupFee: achmarkupFee ?? null,
                achMarkupRate: achMarkupRate ?? null, 
            });

            setPricingModel((prev) => ({
                ...prev,          // keep other properties unchanged
                pricingType: details.pricingType,      // update only the 'merchantAccount' property
            }));

            setPricingModel((prev) => ({
                ...prev,          // keep other properties unchanged
                merchantAccount: details.merchantName,      // update only the 'merchantAccount' property
            }));

            
            fetchMerchantPaymentMetodData();
            setPricingTypeLabel(details.pricingType);
           
            if (details?.pricingType &&              // ensures pricingType exists
                details.pricingType.toUpperCase() === "INTERCHANGE PLUS") {
                rules = rules.filter(rule =>
                    !(rule.cardType.toUpperCase() === option_any &&
                        rule.paymentMethod.toUpperCase() === option_any) // exclude "Any"
                );
            }
            else {
                rules = rules.filter(rule =>
                    !(rule.cardType.toUpperCase() === option_any &&
                        rule.paymentMethod.toUpperCase() === option_any &&
                        rule.shopperInteraction.toUpperCase() === option_any)
                );
            }

            const newRows = rules
                .map(rule => ({
                cardType: rule.cardType,
                brand: rule.cardBrand,
                paymentMethod: rule.paymentMethod,
                interaction: (rule.paymentMethod === paymentMethod_ACH && rule.shopperInteraction ==="ECOMMERCE" ? "Card Not Present":rule.shopperInteraction),
                markupRate: rule.markupRate,
                markupFee: rule.markupFee,
                fixed: (rule.paymentMethod === paymentMethod_ACH ?true:false),
                ruleId: rule.ruleId,
                splitLogicId: rule.splitLogicId
            }));
            setRows(prevRows => [...prevRows, ...newRows]);     
            updateDefaultRow(details)
            setPricingTabAndFields(details, achRuleFound)
        }
    }, [data]);

    //Hook that observe the pricingModelRequest, once the object is set 
    // this will call the save functionlity
    useEffect(() => {
        if (pricingMode === pricingmode_simple || (pricingMode !== pricingmode_simple && editOption !== EditOptions.ALL)) {
            if ((!isEditMode && pricingModelRequest) || (isEditMode && pricingModelRequest_Update))
                SavePricingModelRulesDefinition();
        } 
    }, [pricingMode, pricingModelRequest, pricingModelRequest_Update]);

    const updateDefaultRow = (details) => {
        setRows(prevRows =>
            prevRows.map((row, index) =>
                index === 0
                    ? {
                        ...row,
                        markupRate: details.markupRate.toString(),
                        markupFee: details.markupFee.toString(),                       
                    }
                    : row
            )
        );
    };
    // Handle fetch errors
    useEffect(() => {
        if (pricingError) toast.error("Failed to load pricing model data");
    }, [pricingError]);


    // Add a new row
    const handleAddRow = () => {
        setRows([
            ...rows,
            {
                cardType: "none",
                brand: "none",
                interaction: "none",
                paymentMethod: "none",
                markupRate: null,
                markupFee: null,
                fixed: false,
                ruleId: "",
                splitLogicId: "",
                hasError : false                
            },
        ]);
    };

   
    // Delete a row
    const handleDeleteRow = (index) => {
        setDialogOpen(true);
        setDialogContent(`Are you sure you want to delete this rule ?`);
        setOnConfirm(() => () => {
            setRows(rows.filter((_, i) => i !== index));
            setDialogOpen(false);
        });
    };

    // Handle dropdown / input changes
    const handleChange = (index, field, value) => {
        // For markupRate and markupFee allow decimal only
        if ((field === "markupRate" || field === "markupFee") && value !== "") {
            if (!/^\d*\.?\d*$/.test(value)) return; // prevent invalid input
        }
      
        const updatedRows = [...rows];
        updatedRows[index][field] = value;
        setRows(updatedRows);
    };

    ///ACH 
    const onACHMarkupRateChanged = (event: any) => {
        const { value } = event.target;

        if (/^\d*\.?\d*$/.test(value)) {
            setPricingModel((prev) => ({
                ...prev,          // keep other properties unchanged
                achMarkupRate: value,      // update only the 'achMarkupRate' property
            }));

            // Update the 'fixed: true' row in rows
            setRows((prevRows) =>
                prevRows.map((row) =>
                    row.fixed ? { ...row, achMarkupRate: value } : row
                )
            );
        }
    };

    const onACHMarkupFeeChanged = (event: any) => {
        const { value } = event.target;
        if (/^\d*\.?\d*$/.test(value)) {
            setPricingModel((prev) => ({
                ...prev,          // keep other properties unchanged
                achMarkupFee: value,      // update only the 'achMarkupFee' property
            }));

            // Update the 'fixed: true' row in rows
            setRows((prevRows) =>
                prevRows.map((row) =>
                    row.fixed ? { ...row, achMarkupFee: value } : row
                )
            );
        }
    };
    function setACHRow(pricingModel: any) {
        let achRow = rows.filter(row => row.paymentMethod === paymentMethod_ACH && row.interaction === "Card Not Present");

        if (achRow !== null && achRow.length > 0) {
            if (!isEmpty(pricingModel.achMarkupFee) || !isEmpty(pricingModel.achMarkupRate)) {
                achRow[0].markupRate = pricingModel.achMarkupRate.toString();
                achRow[0].markupFee = pricingModel.achMarkupFee.toString();
            } else {
                achRow[0].markupRate = null;
                achRow[0].markupFee = null;
            }
        }
        else {
            if (!isEmpty(pricingModel.achMarkupFee) || !isEmpty(pricingModel.achMarkupRate)) {
              const  newAchRow = {
                    cardType: "Any",
                    brand: paymentMethod_ACH_DisplayName,
                    interaction: "Card Not Present",
                    markupRate: pricingModel.achMarkupRate.toString(),
                    markupFee: pricingModel.achMarkupFee.toString(),
                    fixed: true,
                    ruleId: "",
                    splitLogicId: "",
                    hasError: false, // ach row cannot be deleted,
                    paymentMethod: paymentMethod_ACH,
                };

                setRows((prevRows) => {
                  const index = 1;
                  return [
                    ...prevRows.slice(0, index),
                    newAchRow,
                    ...prevRows.slice(index),
                  ];
                });
                
            }
        }
    }

    function setACHRowToPricing(pricings: any) {
        let achRow = rows.filter(row => row.paymentMethod === paymentMethod_ACH);
        if (achRow && achRow.length > 0) {
            pricings.push({
                cardBrand: "Any",
                paymentMethod: "ach",
                fundingSource: "Any",
                shopperInteraction: "Any",
                markupFee: achRow[0].markupFee,
                markupRate: achRow[0].markupRate,
                isDefault: false,
                ruleId: achRow[0].ruleId,
                splitLogicId: achRow[0].splitLogicId
            });
        }
    }


    // Save handler
    const handleSave = () => {
        console.log("Pricing data:", rows);
        alert("Pricing data saved! Check console for details.");
    };

    // Styles
    const thStyle = {
        border: "1px solid #ddd",
        padding: "6px",
        backgroundColor: "#f8f8f8",
        width: "120px"
    };

    const thStyle2 = {
        border: "1px solid #ddd",
        padding: "6px",
        backgroundColor: "#f8f8f8",
        width: "120px"
    };

    const thStyle140PX = {
        border: "1px solid #ddd",
        padding: "6px",
        backgroundColor: "#f8f8f8",
        width: "140px"
    };

    const thStyle160PX = {
        border: "1px solid #ddd",
        padding: "6px",
        backgroundColor: "#f8f8f8",
        width: "160px"
    };

    const thAction = {
        border: "1px solid #ddd",
        padding: "6px",
        backgroundColor: "#f8f8f8",
        width: "90px"
    };

    const tdStyle = {
        border: "1px solid #ddd",
        padding: "6px",
        
    };

    const selectStyle = {
        width: "100%",
        padding: "4px",
       
    };

    const inputStyle = {
        width: "100%",
        padding: "4px",
    };

    //
    function setPricingTabAndFields(details: any, achRuleFound: boolean)
    {
        if (details.isSimple) {
            setPricingMode(pricingmode_simple);
        }
        else {
            setPricingMode(pricingmode_enhanced);
        }

        if (details.dealersCount > 0 || details.isSimple) {
            setShowPrcingTab(false);
            setSaveButtonTitle("Save");
        } else if (details.dealersCount <= 0 && !details.isSimple) {
            setShowPrcingTab(true);
            setSaveButtonTitle("Save & Next");
        }

        if (details.dealersCount <= 0)
            setEditOption(EditOptions.ALL); // Allow editing all fields 
        else if (details.dealersCount > 0 && achRuleFound)
            setEditOption(EditOptions.NAME_ONLY); // Allow editing name & description only
        else if (details.dealersCount > 0 && !achRuleFound)
            setEditOption(EditOptions.ACH_AND_NAME_ONLY); // Allow editing name, description and ACH fields only
    };

    const moveItemToTopByValue = (list, value) => {
        const index = list.indexOf(value);
         if (index <= 0) return list;

        return [value, ...list.filter(item => item !== value)];
    };

    return (
        <Box style={{ border: "0px solid red" }}>
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                style={{ padding: "0px", paddingRight: "0px" }}
            >
                {isFetchingData ? (
                    <Box>
                        Please wait. Processing.....
                        <CircularProgress size={25}></CircularProgress>{" "}
                    </Box>
                ) : (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        style={{ padding: "0px", paddingRight: "0px" }}
                    >
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            style={{ padding: "0px", paddingRight: "0px" }}
                            >

                                <Paper sx={{ p: 2 }}>
                                    <Box
                                        style={{
                                            padding: "5px",
                                            fontSize: "18px",
                                            color: "#3366ff",
                                            backgroundColor: "",
                                            borderBottom: "1px solid #EAEDED"
                                        }}
                                    >
                                        {document.title}
                                    </Box>
                                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="split-config-tabs">
                                        <Tab label="Details" />
                                        {(showPrcingTab) && <Tab label="Pricing" />}
                                    </Tabs>

                                    {/* ================= TAB 1 ================= */}
                                    {activeTab === 0 && (
                                        <table style={{ border: "0px solid red" }}>
                                            <tbody>
                                                <tr>
                                                    <td valign="top" style={{ border: "0px solid green" }}>
                                                        <Box
                                                            display="flex"
                                                            justifyContent="center"
                                                            alignItems="center"
                                                            style={{ padding: "0px" }}
                                                        >
                                                            <Box style={{ display: "block" }}>
                                                                <Paper>
                                                                    <Box
                                                                        style={{
                                                                            margin: "20px 5px 0px 15px",
                                                                            border: "0px solid red"
                                                                        }}
                                                                    >
                                                                        <Box style={{ fontSize: "small" }}>

                                                                            <Box style={{ marginTop: "20px" }}>
                                                                                <InputLabel
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    sx={{ fontSize: "small" }}
                                                                                    id="demo-simple-select-label"
                                                                                >

                                                                                    Name *
                                                                                </InputLabel>
                                                                                <TextField
                                                                                    className={classes.root}
                                                                                    variant="standard"
                                                                                    fullWidth
                                                                                    id="outlined-required"
                                                                                    name="name"
                                                                                    value={pricingModel.name}
                                                                                    inputProps={{
                                                                                        style: { fontSize: "small" },
                                                                                        maxLength: 100, // ✅ sets max length to 100 characters
                                                                                    }}
                                                                                    style={{
                                                                                        marginTop: "15px"
                                                                                    }}
                                                                                    onChange={onNameChanged}

                                                                                />
                                                                            </Box>
                                                                        {/* Add this block where you want the radio buttons to appear */}
                                                                        <Box style={{ marginTop: "20px" }}>
                                                                            <InputLabel
                                                                                size="small"
                                                                                variant="standard"
                                                                                sx={{ fontSize: "small" }}
                                                                                id="pricing-mode-label"
                                                                            >
                                                                                Pricing Mode
                                                                            </InputLabel>
                                                                            <RadioGroup
                                                                                row  
                                                                                aria-labelledby="pricing-mode-label"  
                                                                                name="pricing-mode"  
                                                                                value={pricingMode}  
                                                                                onChange={handlePricingModeChange}  
                                                                                style={{ marginTop: "15px" }} 
                                                                                 // Disable when isEditMode is true  
                                                                            >  
                                                                                <FormControlLabel
                                                                                    value="Simple"
                                                                                    disabled={isEditMode}
                                                                                    className={classes.root}
                                                                                    control={<Radio />}
                                                                                    label={
                                                                                        <span style={{ color: "black", fontSize: "small" }}>Simple</span>
                                                                                    }
                                                                                />   
                                                                                <FormControlLabel  
                                                                                    value="Enhanced"  
                                                                                        disabled={isEditMode}  
                                                                                        className={classes.root}
                                                                                    control={<Radio />}  
                                                                                        label={
                                                                                            <span style={{ color: "black", fontSize:"small" }}>Enhanced</span>  
                                                                                    }  
                                                                                />   
                                                                            </RadioGroup>  
                                                                        </Box>
                                                                            <Box style={{ marginTop: "20px" }}>

                                                                                <InputLabel
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    sx={{ fontSize: "small" }}
                                                                                    id="demo-simple-select-label"
                                                                                > Description *
                                                                                </InputLabel>
                                                                                <TextField
                                                                                    className={classes.root}
                                                                                    variant="standard"
                                                                                    fullWidth
                                                                                    id="outlined-required"
                                                                                    name="description"
                                                                                    value={pricingModel.description}
                                                                                    inputProps={{
                                                                                        style: { fontSize: "small" },
                                                                                        maxLength: 100, // ✅ sets max length to 100 characters
                                                                                    }}
                                                                                    style={{
                                                                                        marginTop: "15px"
                                                                                    }}
                                                                                    onChange={onDescriptionChanged}
                                                                                />
                                                                            </Box>
                                                                            <Box>
                                                                            </Box>

                                                                            <Box style={{ marginTop: "20px" }}>
                                                                                <InputLabel
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    sx={{ fontSize: "small" }}
                                                                                    id="pricing-type-label">
                                                                                    Pricing Type *
                                                                                </InputLabel>
                                                                                {!isEditMode && (
                                                                                <Autocomplete
                                                                                    id="combo-box-pricing-type"
                                                                                    autoHighlight
                                                                                    options={[
                                                                                        { name: "Interchange Plus", value: "Interchange Plus" },
                                                                                        { name: "Flat Rate", value: "Flat Rate" }
                                                                                    ]}
                                                                                    value={pricingType}
                                                                                    getOptionLabel={(option: any) => option.name || "Choose One"}
                                                                                    style={{
                                                                                        width: 565,
                                                                                        marginTop: "15px"
                                                                                    }}
                                                                                    className={classes.autoComplete}
                                                                                    renderInput={params => (
                                                                                        <TextField
                                                                                            size="small"
                                                                                            variant="standard"
                                                                                            sx={{ fontSize: "small" }}
                                                                                            {...params}
                                                                                            fullWidth
                                                                                        />
                                                                                    )}

                                                                                    onChange={(event, value) => {
                                                                                        if (!isEmpty(value))
                                                                                            setPricingTypeId(value);
                                                                                    }}
                                                                                    />
                                                                                )}

                                                                                {isEditMode && (
                                                                                    <InputLabel
                                                                                        size="small"
                                                                                        variant="standard"
                                                                                        sx={{ fontSize: "small" }}
                                                                                        id="merchant-account-label"
                                                                                        style={{
                                                                                            width: 565,
                                                                                            marginTop: "15px"
                                                                                        }}
                                                                                    >
                                                                                        {pricingModel.pricingType}
                                                                                    </InputLabel>
                                                                                )}
                                                                            </Box>

                                                                            <Box style={{ marginTop: "20px" }}>
                                                                                <InputLabel
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    sx={{ fontSize: "small" }}
                                                                                    id="merchant-account-label"
                                                                                >

                                                                                    Merchant Account *
                                                                                </InputLabel>
                                                                                {!isEditMode && (
                                                                                    <Autocomplete
                                                                                        id="combo-box-merchant-account"
                                                                                        autoHighlight
                                                                                        options={merchantAccountData}
                                                                                        value={merchantId}
                                                                                        getOptionLabel={(option: IMerchantAccount) =>
                                                                                            option?.name || "Choose One"
                                                                                        }
                                                                                        style={{
                                                                                            width: 565,
                                                                                            marginTop: "15px"
                                                                                        }}
                                                                                        className={classes.autoComplete}
                                                                                        renderInput={(params) => (
                                                                                            <TextField {...params} variant="standard" fullWidth />
                                                                                        )}
                                                                                        onChange={(event, value) => {
                                                                                            if (!isEmpty(value)) {
                                                                                                setMerchantAccount(value);
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                )}

                                                                                {isEditMode && (
                                                                                    <InputLabel
                                                                                        size="small"
                                                                                        variant="standard"
                                                                                        sx={{ fontSize: "small" }}
                                                                                        id="merchant-account-label"
                                                                                    >
                                                                                        {pricingModel.merchantAccount}
                                                                                    </InputLabel>
                                                                                )}
                                                                            </Box>
                                                                            <Box style={{ marginTop: "20px" }}>
                                                                                <InputLabel
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    sx={{ fontSize: "small" }}
                                                                                    id="currency-label"
                                                                                >
                                                                                    Currency
                                                                                </InputLabel>
                                                                                <InputLabel
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    sx={{ fontSize: "small" }}
                                                                                    id="currency-value-label"
                                                                                    style={{
                                                                                        marginTop: "15px"
                                                                                    }}

                                                                                >
                                                                                    {pricingModel.currency}
                                                                                </InputLabel>
                                                                            </Box>


                                                                            <Box style={formStyles.fieldSpacer}>
                                                                                <InputLabel
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    sx={{ fontSize: "small" }}
                                                                                    id="markup-rate-label"                                                                                    
                                                                                > Markup Rate (%) *
                                                                                    <Tooltip title="Enter the default markup percentage applied to all transactions">
                                                                                        <IconButton size="small">
                                                                                            <InfoOutlinedIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </InputLabel>
                                                                                <TextField
                                                                                    className={classes.root}
                                                                                    variant="standard"
                                                                                    fullWidth
                                                                                    id="outlined-required"
                                                                                    name="markupRate"
                                                                                    placeholder="(0.00 - 9.99)"
                                                                                    value={pricingModel.markupRate == null ? "" : pricingModel.markupRate}
                                                                                    inputProps={{
                                                                                        style: { fontSize: "small" }
                                                                                    }}
                                                                                    style={{
                                                                                        marginTop: "15px"
                                                                                    }}
                                                                                    onChange={onMarkupRateChanged}
                                                                                    disabled={isEditMode &&(editOption !== EditOptions.ALL)}
                                                                                />
                                                                            </Box>
                                                                            <Box style={formStyles.fieldSpacer}>
                                                                                <InputLabel
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    sx={{ fontSize: "small" }}
                                                                                    id="markup-fee-label"
                                                                                > Markup fee/transaction *

                                                                                    <Tooltip title="Enter the default markup fee applied to all transactions">
                                                                                        <IconButton size="small">
                                                                                            <InfoOutlinedIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </InputLabel>
                                                                                <TextField
                                                                                    className={classes.root}
                                                                                    variant="standard"
                                                                                    fullWidth
                                                                                    id="outlined-required"
                                                                                    name="markupFee"
                                                                                    placeholder="(0.00 - 9.99)"
                                                                                    value={pricingModel.markupFee == null ? "" : pricingModel.markupFee}
                                                                                    inputProps={{
                                                                                        style: { fontSize: "small" }
                                                                                    }}
                                                                                    style={{
                                                                                        marginTop: "15px"
                                                                                    }}
                                                                                    onChange={onMarkupFeeChanged}
                                                                                    disabled={isEditMode && (editOption !== EditOptions.ALL)}
                                                                                />
                                                                            </Box>
                                                                            <Box style={formStyles.fieldSpacer}>


                                                                                <InputLabel
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    sx={{ fontSize: "small" }}
                                                                                    id="monthly-fee-label"
                                                                                > Monthly fee *
                                                                                </InputLabel>
                                                                                <TextField
                                                                                    className={classes.root}
                                                                                    variant="standard"
                                                                                    fullWidth
                                                                                    id="outlined-required"
                                                                                    name="monthlyFee"
                                                                                    placeholder="(0.00 - 999.99)"
                                                                                    value={pricingModel.monthlyFee == null ? "" : pricingModel.monthlyFee}
                                                                                    inputProps={{
                                                                                        style: { fontSize: "small" }
                                                                                    }}
                                                                                    style={{
                                                                                        marginTop: "15px"
                                                                                    }}
                                                                                    onChange={onMonthlyFeeChanged}
                                                                                    disabled={isEditMode && (editOption !== EditOptions.ALL)}
                                                                                />

                                                                            </Box>
                                                                            {pricingModel.currency.toUpperCase() === "USD" && (<Box style={formStyles.fieldSpacer}>
                                                                                <InputLabel
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    sx={{ fontSize: "small" }}
                                                                                    id="markup-rate-label"
                                                                                > ACH Markup Rate (%) 
                                                                                    <Tooltip title="Enter the default markup percentage applied to all ACH transactions">
                                                                                        <IconButton size="small">
                                                                                            <InfoOutlinedIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </InputLabel>
                                                                                <TextField
                                                                                    className={classes.root}
                                                                                    variant="standard"
                                                                                    fullWidth
                                                                                    id="outlined-required"
                                                                                    name="markupRate"
                                                                                    placeholder="(0.00 - 9.99)"
                                                                                    value={pricingModel.achMarkupRate == null ? "" : pricingModel.achMarkupRate}
                                                                                    inputProps={{
                                                                                        style: { fontSize: "small" }
                                                                                    }}
                                                                                    style={{
                                                                                        marginTop: "15px"
                                                                                    }}
                                                                                    onChange={onACHMarkupRateChanged}
                                                                                    disabled={isEditMode && (editOption === EditOptions.NAME_ONLY)}
                                                                                />
                                                                            </Box>)}
                                                                            {pricingModel.currency.toUpperCase() === "USD" && ( <Box style={formStyles.fieldSpacer}>
                                                                                <InputLabel
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    sx={{ fontSize: "small" }}
                                                                                    id="markup-fee-label"
                                                                                > ACH Markup fee/transaction

                                                                                    <Tooltip title="Enter the default markup fee applied to all ACH transactions">
                                                                                        <IconButton size="small">
                                                                                            <InfoOutlinedIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </InputLabel>
                                                                                <TextField
                                                                                    className={classes.root}
                                                                                    variant="standard"
                                                                                    fullWidth
                                                                                    id="outlined-required"
                                                                                    name="markupFee"
                                                                                    placeholder="(0.00 - 9.99)"
                                                                                    value={pricingModel.achMarkupFee == null ? "" : pricingModel.achMarkupFee}
                                                                                    inputProps={{
                                                                                        style: { fontSize: "small" }
                                                                                    }}
                                                                                    style={{
                                                                                        marginTop: "15px"
                                                                                    }}
                                                                                    onChange={onACHMarkupFeeChanged}
                                                                                    disabled={isEditMode && (editOption === EditOptions.NAME_ONLY)}
                                                                                />
                                                                            </Box>)}

                                                                            <Box
                                                                                style={{
                                                                                    padding: "10px 10px 10px 0px",
                                                                                    textAlign: "left"
                                                                                }}
                                                                            >
                                                                                <table>
                                                                                    <tbody>
                                                                                        <tr>
                                                                                            <td>
                                                                                                <Button
                                                                                                    disabled={isSaveDisabled}
                                                                                                    style={formStyles.fieldSpacer}
                                                                                                    variant="contained"
                                                                                                    onClick={SavePricingModelDefinition}
                                                                                                >
                                                                                                    {saveButtonTitle} 
                                                                                                </Button>
                                                                                            </td>
                                                                                            <td>
                                                                                                {isProcessing && (
                                                                                                    <CircularProgress
                                                                                                        size={25}
                                                                                                        style={{
                                                                                                            marginLeft: "20px",
                                                                                                            marginTop: "20px"
                                                                                                        }}
                                                                                                        color="info"
                                                                                                    />
                                                                                                )}
                                                                                            </td>
                                                                                        </tr>
                                                                                    </tbody>
                                                                                </table>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </Paper>
                                                            </Box>
                                                        </Box>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    
                                    )}


                                    {/* ================= TAB 2 ================= */}
                                    {activeTab === 1 && (
                                        <table style={{ border: "0px solid red" }}>
                                            <tbody>
                                                <tr>
                                                    <td valign="top" style={{ border: "0px solid green" }}>
                                                        <Box
                                                            display="flex"
                                                            justifyContent="center"
                                                            alignItems="center"
                                                            style={{ padding: "0px" }}
                                                        >
                                                            <Box style={{ display: "block" }}>
                                                                <Paper>
                                                                    <Box
                                                                        style={{
                                                                            margin: "20px 5px 0px 15px",
                                                                            border: "0px solid red"
                                                                        }}
                                                                    >
                                                                        <Box style={{ fontSize: "small" }}>
                                                                            <Box>
                                                                                <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
                                                                                    <h3>{pricingTypeLabel} Pricing</h3>
                                                                                    {/* Pricing Table */}
                                                                                    <table
                                                                                        style={{
                                                                                            width: "100%",
                                                                                            borderCollapse: "collapse",
                                                                                            marginTop: "10px"
                                                                                        }}
                                                                                    >
                                                                                        <thead>
                                                                                            <tr>
                                                                                                <th></th>
                                                                                                <th style={thStyle}>Funding Source</th>
                                                                                                <th style={thStyle160PX}>Payment Method</th>
                                                                                                {pricingModel.pricingType === "Flat Rate" && (
                                                                                                    <th style={thStyle140PX}>Shopper Interaction</th>
                                                                                                )}
                                                                                                <th style={thStyle2}>Markup Rate (%)</th>
                                                                                                <th style={thStyle2}>Markup Fee ($)</th>
                                                                                                <th style={thAction}>Action</th>
                                                                                            </tr>
                                                                                        </thead>

                                                                                        <tbody>
                                                                                            {rows.map((row, index) => (
                                                                                                <tr key={index}>
                                                                                                    <td>
                                                                                                        {row.hasError && (
                                                                                                            <span style={{ color: "red", margin: "5px", fontWeight: "bold", fontSize: "18px" }}>*</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                    {/* Card Type */}
                                                                                                    <td style={tdStyle}>
                                                                                                        {row.fixed ? (
                                                                                                            row.cardType
                                                                                                        ) : (
                                                                                                            <select
                                                                                                                value={row.cardType}
                                                                                                                onChange={(e) =>
                                                                                                                    handleChange(index, "cardType", e.target.value)
                                                                                                                }
                                                                                                                style={selectStyle}
                                                                                                            >
                                                                                                                <option value="none"> Choose One </option>
                                                                                                                    <option value={option_any} > Any </option>
                                                                                                                <option value="CREDIT"> Credit </option>
                                                                                                                <option value="DEBIT"> Debit </option>
                                                                                                            </select>
                                                                                                        )}
                                                                                                    </td>

                                                                                                    {/* Brand */}
                                                                                                    <td  style={tdStyle}>
                                                                                                        {row.fixed ? (
                                                                                                            (row.brand === paymentMethod_ACH) ? paymentMethod_ACH_DisplayName : row.brand
                                                                                                        ) : (
                                                                                                            <select
                                                                                                                value={row.brand}
                                                                                                                onChange={(e) =>
                                                                                                                    handleChange(index, "brand", e.target.value)
                                                                                                                }
                                                                                                                style={selectStyle}
                                                                                                                >
                                                                                                                    <option key="0" value="0">Choose One</option>
                                                                                                                    <option key={option_any} value={option_any} >Any</option>
                                                                                                                    {merchantPaymentMethods.map((pm) => {
                                                                                                                        if (pm.code.toLowerCase() === paymentMethod_Interac) {
                                                                                                                            if (row.cardType.toLowerCase() === cardType_debit) {
                                                                                                                                return (
                                                                                                                                    <option key={pm.code} value={pm.code}>{pm.description}</option>
                                                                                                                                );
                                                                                                                            } else return null;                                                                                                                            
                                                                                                                        } else {
                                                                                                                            return (
                                                                                                                                <option key={pm.code} value={pm.code}>{pm.description}</option>
                                                                                                                            );
                                                                                                                        }
                                                                                                                    })}
                                                                                                            </select>
                                                                                                        )}
                                                                                                    </td>

                                                                                                    {/* Interaction */}
                                                                                                    {pricingModel.pricingType === "Flat Rate" && (
                                                                                                    <td style={tdStyle}>
                                                                                                        {row.fixed ? (
                                                                                                            row.interaction
                                                                                                        ) : (
                                                                                                            <select
                                                                                                                value={row.interaction}
                                                                                                                onChange={(e) =>
                                                                                                                    handleChange(index, "interaction", e.target.value)
                                                                                                                }
                                                                                                                style={selectStyle}
                                                                                                                >
                                                                                                                        <option value="none">Choose One</option>
                                                                                                                        <option value={option_any } >Any</option>
                                                                                                                        <option value="POS" title="Payment made at POS">Card Present</option>
                                                                                                                        <option value="ECOMMERCE" title="Payment made online / Pay by link">Card Not Present</option>
                                                                                                                        <option value="SAVEDCARD" title="Payment using a previously saved card">Saved Card</option>
                                                                                                            </select>
                                                                                                        )}
                                                                                                    </td>
                                                                                                    )}
                                                                                                    {/* Markup Rate */}
                                                                                                    <td style={tdStyle}>
                                                                                                        {row.fixed ? (
                                                                                                            row.markupRate
                                                                                                        ) : (
                                                                                                            <input
                                                                                                                    type="text"
                                                                                                                    placeholder="(0.00 - 9.99)"
                                                                                                                value={row.markupRate == null ? "" : row.markupRate}
                                                                                                                onChange={(e) =>
                                                                                                                    handleChange(index, "markupRate", e.target.value)
                                                                                                                }
                                                                                                                style={inputStyle}
                                                                                                            />
                                                                                                        )}
                                                                                                    </td>

                                                                                                    {/* Markup Fee */}
                                                                                                    <td style={tdStyle}>
                                                                                                        {row.fixed ? (
                                                                                                            row.markupFee
                                                                                                        ) : (
                                                                                                            <input
                                                                                                                type="text"
                                                                                                                placeholder="(0.00 - 9.99)"                                                                                                                    
                                                                                                                value={row.markupFee == null ? "" : row.markupFee}
                                                                                                                onChange={(e) =>
                                                                                                                    handleChange(index, "markupFee", e.target.value)
                                                                                                                }
                                                                                                                style={inputStyle}
                                                                                                            />
                                                                                                        )}
                                                                                                    </td>

                                                                                                    {/* Action */}
                                                                                                    <td style={tdStyle}>
                                                                                                        {!row.fixed && (
                                                                                                             <Button
                                                                                                                size="small"
                                                                                                                variant="outlined"
                                                                                                                color="error"
                                                                                                                onClick={() => handleDeleteRow(index)}
                                                                                                            >
                                                                                                                Delete
                                                                                                                </Button>
                                                                                                        )}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ))}

                                                                                            
                                                                                            <tr>
                                                                                                <td></td>
                                                                                                {/* Card Type */}
                                                                                                <td style={tdStyle}>
                                                                                                       
                                                                                                    </td>

                                                                                                    {/* Brand */}
                                                                                                <td style={tdStyle}>
                                                                                                        
                                                                                                </td>
                                                                                                {/* Shopper Interaction */}
                                                                                                {pricingModel.pricingType === "Flat Rate" && (
                                                                                                    <td style={tdStyle}>
                                                                                                    </td>
                                                                                                )}
                                                                                                    {/* Markup Rate */}
                                                                                                    <td style={tdStyle}>
                                                                                                        
                                                                                                    </td>

                                                                                                    {/* Markup Fee */}
                                                                                                    <td style={tdStyle}>
                                                                                                        
                                                                                                    </td>

                                                                                                    {/* Action */}
                                                                                                    <td style={tdStyle}>
                                                                                                    {/* Add row button */}

                                                                                                    <Button
                                                                                                        disabled={isSaveDisabled}
                                                                                                        size="small"
                                                                                                        variant="contained"
                                                                                                        color="primary"
                                                                                                        onClick={handleAddRow}
                                                                                                    >
                                                                                                        +
                                                                                                    </Button>
                                                                                                    </td>
                                                                                                </tr>
                                                                                           
                                                                                        </tbody>
                                                                                    </table>
                                                                                    
                                                                                </div>
                                                                            </Box>
                                                                            <Box
                                                                                style={{
                                                                                    padding: "10px 10px 10px 0px",
                                                                                    textAlign: "left"
                                                                                }}
                                                                            >
                                                                                <table>
                                                                                    <tbody>
                                                                                        <tr>
                                                                                            <td>
                                                                                                <Button
                                                                                                    disabled={isSaveDisabled || isProcessing}
                                                                                                    style={formStyles.fieldSpacer}
                                                                                                    variant="contained"
                                                                                                    onClick={SavePricingModelRulesDefinition}
                                                                                                >
                                                                                                    Save
                                                                                                </Button>
                                                                                            </td>
                                                                                            <td>
                                                                                                {isProcessing && (
                                                                                                    <CircularProgress
                                                                                                        size={25}
                                                                                                        style={{
                                                                                                            marginLeft: "20px",
                                                                                                            marginTop: "20px"
                                                                                                        }}
                                                                                                        color="info"
                                                                                                    />
                                                                                                )}
                                                                                            </td>
                                                                                        </tr>
                                                                                    </tbody>
                                                                                </table>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </Paper>
                                                            </Box>
                                                        </Box>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>

                                    )}

                                   
                                </Paper>
                           
                        </Box>
                    </Box>
                )}
            </Box>
            <ConfirmDialog
                open={dialogOpen}
                title={dialogTitle}
                content={dialogContent}
                onClose={() => setDialogOpen(false)}
                onConfirm={onConfirm}
                confirmLabel={dialogProps.confirmLabel}
                confirmColor={dialogProps.confirmColor}
                showConfirm={dialogProps.showConfirm}
                cancelLabel={dialogProps.cancelLabel}
                isProcessing={dialogProps.isProcessing}
                processingContent={dialogProps.processingContent}
            />
        </Box>
    );
}

export default SplitConfigurationMaster